import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogIn, Home } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  /* ===============================
     DETECCIÓN DE RUTAS
  =============================== */
  const isLandingAhorro = location.pathname === "/ahorro";

  const isPlatformRoute =
    location.pathname.startsWith("/perfil") ||
    location.pathname.startsWith("/servicios/control") ||
    location.pathname.startsWith("/reportes") ||
    location.pathname.startsWith("/configuracion") ||
    location.pathname.startsWith("/lista-espera");

  /* ===============================
     AUTH
  =============================== */
  useEffect(() => {
    checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAuth = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    setIsLoggedIn(!!session);
  };

  const handleLogout = async () => {
    try {
      setIsLoggedIn(false);
      const { error } = await supabase.auth.signOut();
      if (error) console.log("Logout error handled:", error.message);
      toast.success("Sesión cerrada correctamente");
      navigate("/");
    } catch (error) {
      toast.success("Sesión cerrada correctamente");
      navigate("/");
    }
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  /* ===============================
     MENÚ NORMAL
  =============================== */
  const menuItems = [
    { text: "Inicio", href: "/" },
    { text: "Nosotros", href: "/nosotros" },
    { text: "Servicios", href: "/servicios" },
    { text: "Blog", href: "/blog" },
    { text: "Contacto", href: "/contacto" },
  ];

  const dashboardMenuItems =
    isLoggedIn && !isPlatformRoute
      ? [{ text: "Mi Plataforma", href: "/perfil" }]
      : [];

  const allMenuItems = [...menuItems, ...dashboardMenuItems];

  const goToAdmin = () => {
    window.location.replace("https://sergen.pe/admin/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-[60] bg-white/90 backdrop-blur-md shadow-sm transition-all duration-300">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">

          {/* LOGO */}
          <Link to="/" className="flex items-center">
            <img
              src="/lovable-uploads/12ae4369-c1d4-4066-b8c6-b5d4060a8d15.png"
              alt="Sergen Logo"
              className="h-12 sm:h-14 md:h-16 w-auto object-contain transition-transform duration-300 hover:scale-105"
            />
          </Link>

          {/* ===== MENÚ DESKTOP ===== */}
          <div className="hidden md:flex items-center space-x-4">

            {isLandingAhorro ? (
              <>
                <Link
                  to="/"
                  aria-label="Inicio"
                  className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition"
                >
                  <Home size={18} />
                </Link>

                {!isLoggedIn ? (
                  <Button
                    onClick={goToAdmin}
                    className="bg-gray-900 text-white hover:bg-gray-800 transition"
                  >
                    Iniciar sesión
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate("/perfil")}
                    className="bg-gray-900 text-white hover:bg-gray-800 transition"
                  >
                    Mi plataforma
                  </Button>
                )}
              </>
            ) : (
              <>
                {allMenuItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="text-gray-700 hover:text-[#F5A524] font-medium transition-colors"
                  >
                    {item.text}
                  </Link>
                ))}

                {isLoggedIn ? (
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="text-[#F5A524] hover:bg-[#F5A524] hover:text-white transition"
                  >
                    Cerrar Sesión
                  </Button>
                ) : (
                  <Button
                    onClick={goToAdmin}
                    variant="outline"
                    className="text-[#F5A524] border-transparent hover:bg-[#F5A524] hover:text-white flex items-center gap-2 transition"
                  >
                    <LogIn size={16} /> Iniciar Sesión
                  </Button>
                )}
              </>
            )}
          </div>

          {/* ===== MENÚ MÓVIL ===== */}
          {!isLandingAhorro && (
            <button
              onClick={toggleMenu}
              className="md:hidden text-gray-700 hover:text-[#F5A524]"
            >
              {isOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          )}
        </div>
      </div>

      {/* ===== MENÚ MÓVIL DESPLEGABLE ===== */}
      {!isLandingAhorro && isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="md:hidden bg-white border-t shadow-md"
        >
          <div className="container mx-auto px-4 py-4 space-y-4">
            {allMenuItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="block text-gray-700 hover:text-[#F5A524] font-medium transition"
                onClick={() => setIsOpen(false)}
              >
                {item.text}
              </Link>
            ))}

            {isLoggedIn ? (
              <button
                onClick={() => {
                  handleLogout();
                  setIsOpen(false);
                }}
                className="block w-full text-left text-gray-700 hover:text-[#F5A524] transition"
              >
                Cerrar Sesión
              </button>
            ) : (
              <button
                onClick={goToAdmin}
                className="block w-full text-left text-gray-700 hover:text-[#F5A524] transition flex items-center gap-2"
              >
                <LogIn size={16} /> Iniciar Sesión
              </button>
            )}
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;
